import { RefObject, useCallback, useState } from "react";
import { FormikProps } from "formik";
import { useWorkService } from "../services/work.service";
import { FinishWorkDTO, ValidationForm } from "../types/work.types";
import { handleError } from "../common/utils";

interface UseWorkValidationOptions {
  onSuccess?: () => void;
  onError?: (message: string) => void;
  setLoading?: (loading: boolean) => void;
}

interface ValidationState {
  showValidationWarning: boolean;
  validationWarningMessage: string;
  searchParams?: Record<string, string>;
  pendingWorkSubmission: {
    workId: string;
    workData: FinishWorkDTO;
    onSuccess?: () => void;
  } | null;
}

export function useWorkValidation(options: UseWorkValidationOptions = {}) {
  const { updateWork, updateWorkWithOverride } = useWorkService();
  const { onSuccess, onError, setLoading } = options;

  const [validationState, setValidationState] = useState<ValidationState>({
    showValidationWarning: false,
    validationWarningMessage: "",
    pendingWorkSubmission: null,
  });

  const submitWorkWithValidation = useCallback(
    async (workId: string, workData: FinishWorkDTO, successCallback?: () => void): Promise<void> => {
      if (setLoading) setLoading(true);
      if (onError) onError("");

      try {
        await updateWork(workId, workData);
        if (setLoading) setLoading(false);
        if (successCallback) successCallback();
        if (onSuccess) onSuccess();
      } catch (error: any) {
        if (setLoading) setLoading(false);
        if (error.response?.status === 400) {
          // Show validation warning dialog
          const responseData = error.response.data;
          const message = typeof responseData === 'string' ? responseData : responseData?.message || "Validation warning occurred";
          const searchParams = typeof responseData === 'object' ? responseData?.searchParams : undefined;

          setValidationState({
            showValidationWarning: true,
            validationWarningMessage: message,
            searchParams,
            pendingWorkSubmission: { workId, workData, onSuccess: successCallback },
          });
        } else {
          if (onError) {
            handleError(error, onError, setLoading);
          } else {
            throw error;
          }
        }
      }
    },
    [updateWork, onSuccess, onError, setLoading]
  );

  const handleValidationOverride = useCallback(async () => {
    if (!validationState.pendingWorkSubmission) return;

    setValidationState((prev) => ({ ...prev, showValidationWarning: false }));
    if (setLoading) setLoading(true);
    if (onError) onError("");

    try {
      await updateWorkWithOverride(
        validationState.pendingWorkSubmission.workId,
        validationState.pendingWorkSubmission.workData
      );
      if (setLoading) setLoading(false);
      if (validationState.pendingWorkSubmission.onSuccess) {
        validationState.pendingWorkSubmission.onSuccess();
      }
      if (onSuccess) onSuccess();
    } catch (error) {
      if (onError) {
        handleError(error, onError, setLoading);
      } else {
        throw error;
      }
    } finally {
      setValidationState({
        showValidationWarning: false,
        validationWarningMessage: "",
        pendingWorkSubmission: null,
      });
    }
  }, [updateWorkWithOverride, validationState.pendingWorkSubmission, onSuccess, onError, setLoading]);

  const handleValidationCancel = useCallback(() => {
    setValidationState({
      showValidationWarning: false,
      validationWarningMessage: "",
      pendingWorkSubmission: null,
    });
  }, []);

  const submitWorkIfDirty = useCallback(
    async (
      workId: string,
      workData: FinishWorkDTO,
      formikRef: RefObject<FormikProps<ValidationForm>> | null,
      successCallback?: () => void
    ): Promise<void> => {
      return submitWorkWithValidation(workId, workData, successCallback);
    },
    [submitWorkWithValidation]
  );

  return {
    submitWorkWithValidation,
    submitWorkIfDirty,
    handleValidationOverride,
    handleValidationCancel,
    showValidationWarning: validationState.showValidationWarning,
    validationWarningMessage: validationState.validationWarningMessage,
    validationSearchParams: validationState.searchParams,
  };
}
